import {
  type EventContentObjectDetail,
  type ProgramEventInviteeRole,
  type ProgramUserRsvp,
  isRegisteredProgramInvitee,
} from 'models';
import { Empty, Icon, InfinityLoading, Skeleton } from 'ui';
import { useDebounce } from 'common/hooks';
import { useEventInviteesInfiniteQuery } from '../../../queries';
import { EventInviteesListContentWrapper } from '../../event-invitees-list-content-wrapper';
import { EventAttendee } from './event-attendee';

type P = {
  eventContentObject: EventContentObjectDetail;
  filter: {
    role?: ProgramEventInviteeRole;
    search?: string;
    status?: ProgramUserRsvp;
  };
};

const EventInviteesList = ({ eventContentObject, filter: { role, search: propertySearch, status } }: P) => {
  const { isDebouncing, value: search } = useDebounce(propertySearch);
  const {
    data: allInviteesData,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isLoading,
  } = useEventInviteesInfiniteQuery({
    filter: { eventId: eventContentObject.event.id, role, search, status },
    pagination: { pageSize: 10 },
  });

  const allInvitees = allInviteesData?.pages.flat().flatMap((inviteesPaginated) => inviteesPaginated.data);

  return (
    <>
      {isLoading || isDebouncing || !allInvitees ? (
        <Skeleton active paragraph={{ rows: 14 }} title={false} />
      ) : (
        <EventInviteesListContentWrapper>
          {allInvitees.length === 0 ? (
            <Empty
              description={{
                subtitle: 'Try refining your search or adjusting the filters',
                title: 'No results found',
              }}
              image={<Icon icon="inbox-line" />}
            />
          ) : (
            <>
              {allInvitees.map((invitee) => (
                <EventAttendee
                  eventContentObject={eventContentObject}
                  invitee={invitee}
                  key={isRegisteredProgramInvitee(invitee) ? invitee.id : invitee.email}
                />
              ))}

              <InfinityLoading
                hasMoreData={hasNextPage}
                isLoading={isFetching}
                onEnter={fetchNextPage}
                spinnerSize="small"
              />
            </>
          )}
        </EventInviteesListContentWrapper>
      )}
    </>
  );
};

export default EventInviteesList;
